/**
 * FoundationPasteImportModal — paste text to build an entire Foundation Profile in one async run.
 */

import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { AlertTriangle, CheckCircle2, ClipboardCopy, FileText, Loader2, Sparkles } from 'lucide-react'
import toast from 'react-hot-toast'
import { Button } from '@/components/ui/button'
import { ResearchModalShell } from '@/components/shared/ResearchModalShell'
import type { PersuasionRun } from '@/services/persuasion-lab'
import {
    buildPasteTemplate,
    foundationPasteImportService,
    PASTE_IMPORT_STEPS,
    type FoundationPasteImportMode,
    type FoundationPasteImportPreflight,
} from '@/services/persuasion-lab/foundation-paste-import'
import { useFoundationPasteImport } from '../../context/FoundationPasteImportContext'

type TabId = 'guide' | 'paste' | 'options' | 'progress'

interface FoundationPasteImportModalProps {
    runId: string
    run?: PersuasionRun | null
    onComplete?: () => void
}

export const FoundationPasteImportModal: FC<FoundationPasteImportModalProps> = ({
    runId,
    run,
    onComplete,
}) => {
    const {
        isOpen,
        closeModal,
        initialTab,
        progress,
        jobId,
        estimatedCredits,
        setImportSession,
        isImportActive,
        clearImportSession,
        recoverStuckImport,
    } = useFoundationPasteImport()

    const anchorType = (run?.cpp_anchor_type || 'company') as 'company' | 'product' | 'person'
    const [tab, setTab] = useState<TabId>('guide')
    const [pastedContent, setPastedContent] = useState('')
    const [additionalContext, setAdditionalContext] = useState('')
    const [mode, setMode] = useState<FoundationPasteImportMode>('fill_blanks')
    const [confirmReplace, setConfirmReplace] = useState(false)
    const [submitting, setSubmitting] = useState(false)
    const [preflight, setPreflight] = useState<FoundationPasteImportPreflight | null>(null)
    const [preflightLoading, setPreflightLoading] = useState(false)

    const template = useMemo(() => buildPasteTemplate(anchorType), [anchorType])
    const charCount = pastedContent.length + additionalContext.length

    const resetForm = useCallback(() => {
        setTab('guide')
        setPastedContent('')
        setAdditionalContext('')
        setMode('fill_blanks')
        setConfirmReplace(false)
        setPreflight(null)
        setPreflightLoading(false)
    }, [])

    useEffect(() => {
        if (!isOpen) return
        // Only auto-open progress when explicitly requested or an import is still running
        if (initialTab === 'progress' || isImportActive) {
            setTab('progress')
            return
        }
        setTab(initialTab)
    }, [isOpen, initialTab, isImportActive])

    useEffect(() => {
        if (!isOpen || tab !== 'options' || charCount < 80) {
            setPreflight(null)
            return
        }

        let cancelled = false
        setPreflightLoading(true)
        const timer = setTimeout(async () => {
            try {
                const { preflight: result } = await foundationPasteImportService.preflightImport(runId, {
                    pastedContent: pastedContent.trim(),
                    additionalContext: additionalContext.trim() || undefined,
                    mode,
                })
                if (!cancelled) setPreflight(result)
            } catch {
                if (!cancelled) setPreflight(null)
            } finally {
                if (!cancelled) setPreflightLoading(false)
            }
        }, 400)

        return () => {
            cancelled = true
            clearTimeout(timer)
        }
    }, [isOpen, tab, charCount, runId, pastedContent, additionalContext, mode])

    const handleCopyTemplate = async () => {
        try {
            await navigator.clipboard.writeText(template)
            toast.success('Template copied to clipboard')
        } catch {
            toast.error('Could not copy template')
        }
    }

    const handleStartImport = async () => {
        const combined = [pastedContent.trim(), additionalContext.trim()].filter(Boolean).join('\n\n')
        if (combined.length < 80) {
            toast.error('Paste at least 80 characters of profile content')
            return
        }
        if (mode === 'replace_all' && !confirmReplace) {
            toast.error('Confirm replace-all before continuing')
            setTab('options')
            return
        }

        const blockingIssue = preflight?.issues.find(i => i.severity === 'error')
        if (blockingIssue) {
            toast.error(blockingIssue.message)
            setTab('options')
            return
        }

        if (isImportActive && jobId) {
            toast('Import already in progress — opening progress view')
            setTab('progress')
            return
        }

        setSubmitting(true)
        try {
            const result = await foundationPasteImportService.startImport(runId, {
                pastedContent: pastedContent.trim(),
                additionalContext: additionalContext.trim() || undefined,
                mode,
                confirmReplace: mode === 'replace_all' ? true : undefined,
            })
            if (!result?.jobId) {
                throw new Error('Import started but the server did not return a job id')
            }
            setImportSession(
                result.jobId,
                { jobId: result.jobId, status: 'queued', label: 'Queued…', percent: 0 },
                result.estimatedCredits
            )
            setTab('progress')
        } catch (err: any) {
            const existingJobId = err?.response?.data?.jobId
            if (err?.response?.status === 409 && existingJobId) {
                setImportSession(existingJobId, {
                    jobId: existingJobId,
                    status: 'running',
                    label: 'Import in progress…',
                })
                setTab('progress')
                toast('Resuming in-progress import')
                return
            }
            const msg = err?.response?.data?.error || err?.message || 'Failed to start import'
            toast.error(msg)
        } finally {
            setSubmitting(false)
        }
    }

    const handleClose = () => {
        if (isImportActive) {
            toast('Import continues in the background — use View progress anytime', { icon: 'ℹ️' })
        }
        closeModal()
    }

    const stepLabel =
        tab === 'guide'
            ? 'Step 1 — Question guide'
            : tab === 'paste'
              ? 'Step 2 — Paste your answers'
              : tab === 'options'
                ? 'Step 3 — Import options'
                : 'Import in progress'

    return (
        <ResearchModalShell
            isOpen={isOpen}
            onClose={handleClose}
            title='Build from pasted text'
            stepLabel={stepLabel}
            disableClose={submitting}
        >
            {isImportActive && tab !== 'progress' && (
                <div className='mb-4 rounded-lg border border-cz-purple-200 bg-cz-purple-50 px-3 py-2 text-sm text-cz-purple-900'>
                    An import is running in the background.{' '}
                    <button type='button' className='font-semibold underline' onClick={() => setTab('progress')}>
                        View progress
                    </button>
                </div>
            )}

            <div className='flex flex-wrap gap-2 mb-6 border-b border-gray-200 pb-3'>
                {(['guide', 'paste', 'options'] as TabId[]).map(id => (
                    <button
                        key={id}
                        type='button'
                        onClick={() => setTab(id)}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                            tab === id
                                ? 'bg-cz-purple-100 text-cz-purple-800'
                                : 'text-gray-600 hover:bg-gray-100'
                        }`}
                    >
                        {id === 'guide' ? 'Question guide' : id === 'paste' ? 'Paste text' : 'Options'}
                    </button>
                ))}
                {progress && (
                    <button
                        type='button'
                        onClick={() => setTab('progress')}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                            tab === 'progress'
                                ? 'bg-cz-purple-100 text-cz-purple-800'
                                : 'text-gray-600 hover:bg-gray-100'
                        }`}
                    >
                        {isImportActive ? 'Progress' : 'Last import'}
                    </button>
                )}
            </div>

            {tab === 'guide' && (
                <div className='space-y-4'>
                    <p className='text-sm text-gray-600'>
                        Fill out the template below (externally or here), then paste it on the next tab. List{' '}
                        <strong>every communication target</strong> — each gets full deep research, situations, and TFD.
                    </p>
                    <pre className='text-xs bg-white border border-gray-200 rounded-lg p-4 overflow-x-auto whitespace-pre-wrap max-h-64'>
                        {template}
                    </pre>
                    <div className='flex gap-3'>
                        <Button type='button' variant='outline' onClick={handleCopyTemplate}>
                            <ClipboardCopy className='w-4 h-4 mr-2' /> Copy blank template
                        </Button>
                        <Button type='button' variant='primary' onClick={() => setTab('paste')}>
                            Continue to paste →
                        </Button>
                    </div>
                </div>
            )}

            {tab === 'paste' && (
                <div className='space-y-4'>
                    <label className='block text-sm font-medium text-gray-700'>Pasted profile content</label>
                    <textarea
                        value={pastedContent}
                        onChange={e => setPastedContent(e.target.value)}
                        rows={14}
                        className='w-full rounded-lg border border-gray-300 px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-cz-purple-500 focus:border-transparent'
                        placeholder='Paste your completed foundational profile here…'
                    />
                    <label className='block text-sm font-medium text-gray-700'>Additional context (optional)</label>
                    <textarea
                        value={additionalContext}
                        onChange={e => setAdditionalContext(e.target.value)}
                        rows={3}
                        className='w-full rounded-lg border border-gray-300 px-3 py-2 text-sm'
                        placeholder='Any extra notes for the AI…'
                    />
                    <p className='text-xs text-gray-500'>{charCount} characters (minimum 80)</p>
                    <div className='flex justify-between gap-3'>
                        <Button type='button' variant='outline' onClick={() => setTab('guide')}>
                            ← Back
                        </Button>
                        <Button type='button' variant='primary' onClick={() => setTab('options')}>
                            Continue to options →
                        </Button>
                    </div>
                </div>
            )}

            {tab === 'options' && (
                <div className='space-y-5'>
                    {(preflightLoading || preflight) && (
                        <div className='rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-2'>
                            <p className='text-sm font-semibold text-gray-800'>Pre-flight check</p>
                            {preflightLoading && !preflight ? (
                                <p className='text-sm text-gray-600 flex items-center gap-2'>
                                    <Loader2 className='w-4 h-4 animate-spin' /> Checking this run…
                                </p>
                            ) : preflight ? (
                                <>
                                    <p className='text-sm text-gray-700'>
                                        Paste: ~{preflight.pastedTargetCount} target(s)
                                        {preflight.existingTargetCount > 0
                                            ? ` · Run already has ${preflight.existingTargetCount}`
                                            : ''}
                                        {' · ~'}
                                        {preflight.estimatedCredits} credits
                                    </p>
                                    {preflight.suggestReplaceAll && mode === 'fill_blanks' && (
                                        <p className='text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2'>
                                            This run already has targets. For a revised target list (new scene names),
                                            choose <strong>Replace everything</strong> to avoid duplicate audiences.
                                        </p>
                                    )}
                                    {preflight.issues.map(issue => (
                                        <p
                                            key={issue.code}
                                            className={`text-sm rounded-lg px-3 py-2 ${
                                                issue.severity === 'error'
                                                    ? 'text-red-900 bg-red-50 border border-red-200'
                                                    : issue.severity === 'warning'
                                                      ? 'text-amber-900 bg-amber-50 border border-amber-200'
                                                      : 'text-gray-700 bg-white border border-gray-200'
                                            }`}
                                        >
                                            {issue.message}
                                        </p>
                                    ))}
                                </>
                            ) : null}
                        </div>
                    )}

                    <fieldset className='space-y-3'>
                        <legend className='text-sm font-semibold text-gray-800'>Merge mode</legend>
                        <label className='flex items-start gap-3 p-3 rounded-lg border border-gray-200 cursor-pointer'>
                            <input
                                type='radio'
                                name='mergeMode'
                                checked={mode === 'fill_blanks'}
                                onChange={() => setMode('fill_blanks')}
                                className='mt-1'
                            />
                            <span>
                                <span className='font-medium text-gray-900'>Fill blanks</span>
                                <p className='text-sm text-gray-600 mt-0.5'>
                                    Keep existing data; add only targets whose name/slug is not already on the run.
                                    Best for first import or adding audiences — not for replacing a full target set.
                                </p>
                            </span>
                        </label>
                        <label className='flex items-start gap-3 p-3 rounded-lg border border-amber-200 bg-amber-50 cursor-pointer'>
                            <input
                                type='radio'
                                name='mergeMode'
                                checked={mode === 'replace_all'}
                                onChange={() => setMode('replace_all')}
                                className='mt-1'
                            />
                            <span>
                                <span className='font-medium text-amber-900'>Replace everything</span>
                                <p className='text-sm text-amber-800 mt-0.5'>
                                    Overwrite communication targets, snapshots, symbols, ALO, claims, and value props.
                                </p>
                            </span>
                        </label>
                    </fieldset>

                    {mode === 'replace_all' && (
                        <label className='flex items-start gap-2 p-3 rounded-lg border border-red-200 bg-red-50'>
                            <input
                                type='checkbox'
                                checked={confirmReplace}
                                onChange={e => setConfirmReplace(e.target.checked)}
                                className='mt-1'
                            />
                            <span className='text-sm text-red-900'>
                                <AlertTriangle className='w-4 h-4 inline mr-1' />
                                I understand this will replace existing foundation data on this run.
                            </span>
                        </label>
                    )}

                    <p className='text-sm text-gray-600'>
                        Estimated AI credits: ~
                        {preflight?.estimatedCredits ?? '24+'} (from pasted target count). Credits deduct per step as
                        the import runs.
                    </p>

                    <div className='flex justify-between gap-3'>
                        <Button type='button' variant='outline' onClick={() => setTab('paste')}>
                            ← Back
                        </Button>
                        <Button
                            type='button'
                            variant='primary'
                            disabled={submitting || charCount < 80 || isImportActive}
                            onClick={handleStartImport}
                        >
                            {submitting ? (
                                <>
                                    <Loader2 className='w-4 h-4 mr-2 animate-spin' /> Starting…
                                </>
                            ) : (
                                <>
                                    <Sparkles className='w-4 h-4 mr-2' /> Start import
                                </>
                            )}
                        </Button>
                    </div>
                </div>
            )}

            {tab === 'progress' && !progress && (
                <div className='rounded-lg border border-gray-200 bg-gray-50 px-4 py-6 text-sm text-gray-600'>
                    No import in progress. Use the steps above to paste a profile and start a new import.
                </div>
            )}

            {tab === 'progress' && progress && (
                <div className='space-y-6'>
                    <div className='rounded-xl border border-cz-stroke-subtle bg-accent p-4'>
                        <div className='flex items-center gap-3 mb-2'>
                            {progress.status === 'complete' ? (
                                <CheckCircle2 className='w-6 h-6 text-emerald-600' />
                            ) : progress.status === 'failed' ? (
                                <AlertTriangle className='w-6 h-6 text-red-600' />
                            ) : (
                                <Loader2 className='w-6 h-6 text-cz-purple-700 animate-spin' />
                            )}
                            <div>
                                <p className='font-semibold text-foreground'>{progress.label || 'Working…'}</p>
                                {progress.detail && (
                                    <p className='text-sm text-muted-foreground'>{progress.detail}</p>
                                )}
                                {progress.error && (
                                    <p className='text-sm text-red-600 mt-1'>{progress.error}</p>
                                )}
                            </div>
                        </div>
                        <div className='h-2 bg-gray-200 rounded-full overflow-hidden'>
                            <div
                                className='h-full bg-cz-purple-700 transition-all duration-500'
                                style={{ width: `${progress.percent ?? 10}%` }}
                            />
                        </div>
                        {estimatedCredits != null && progress.status === 'running' && (
                            <p className='text-xs text-muted-foreground mt-2'>
                                Credit hold at start: ~{estimatedCredits} AI credits (from pasted target count; actual usage may differ as steps run).
                            </p>
                        )}
                        {isImportActive && (
                            <p className='text-xs text-muted-foreground mt-2'>
                                You can close this window — the import keeps running. Use the banner or header button to return.
                            </p>
                        )}
                    </div>

                    <ul className='space-y-2'>
                        {PASTE_IMPORT_STEPS.map((step, idx) => {
                            const currentIdx = PASTE_IMPORT_STEPS.findIndex(s => s.id === progress.step)
                            const done = progress.status === 'complete' || (currentIdx >= 0 && idx < currentIdx)
                            const active = progress.step === step.id && progress.status === 'running'
                            return (
                                <li
                                    key={step.id}
                                    className={`flex items-center gap-2 text-sm px-3 py-2 rounded-lg ${
                                        active ? 'bg-cz-purple-100/60 font-medium' : done ? 'text-emerald-700' : 'text-gray-500'
                                    }`}
                                >
                                    {done ? (
                                        <CheckCircle2 className='w-4 h-4 shrink-0' />
                                    ) : active ? (
                                        <Loader2 className='w-4 h-4 shrink-0 animate-spin' />
                                    ) : (
                                        <FileText className='w-4 h-4 shrink-0 opacity-40' />
                                    )}
                                    {step.label}
                                </li>
                            )
                        })}
                    </ul>

                    {progress.status === 'complete' && progress.summary && (
                        <div className='rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900'>
                            <p className='font-semibold mb-2'>Summary</p>
                            <ul className='space-y-1'>
                                <li>{progress.summary.targets} target(s)</li>
                                <li>{progress.summary.tfd} TFD snapshot(s)</li>
                                <li>{progress.summary.alo} ALO item(s) added</li>
                                <li>{progress.summary.claims} claim(s)</li>
                                <li>{progress.summary.briefs} brief(s)</li>
                            </ul>
                        </div>
                    )}

                    {progress.status === 'complete' && progress.warnings && progress.warnings.length > 0 && (
                        <div className='rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900'>
                            <p className='font-semibold mb-2 flex items-center gap-2'>
                                <AlertTriangle className='w-4 h-4' /> Warnings
                            </p>
                            <ul className='space-y-1 list-disc pl-5'>
                                {progress.warnings.map((w, i) => (
                                    <li key={i}>{w}</li>
                                ))}
                            </ul>
                        </div>
                    )}

                    <div className='flex justify-end gap-3'>
                        {progress.status === 'complete' ? (
                            <Button
                                type='button'
                                variant='primary'
                                onClick={() => {
                                    onComplete?.()
                                    resetForm()
                                    closeModal()
                                }}
                            >
                                Review profile
                            </Button>
                        ) : progress.status === 'failed' ? (
                            <>
                                <Button
                                    type='button'
                                    variant='outline'
                                    onClick={async () => {
                                        await clearImportSession()
                                        resetForm()
                                        setTab('paste')
                                    }}
                                >
                                    Try again
                                </Button>
                                <Button type='button' variant='outline' onClick={handleClose}>
                                    Close
                                </Button>
                            </>
                        ) : (
                            <>
                                {progress.step === 'briefs' && (
                                    <Button
                                        type='button'
                                        variant='primary'
                                        onClick={async () => {
                                            try {
                                                await recoverStuckImport()
                                                onComplete?.()
                                                resetForm()
                                                closeModal()
                                            } catch (err: unknown) {
                                                const msg =
                                                    (err as { response?: { data?: { error?: string } } })?.response
                                                        ?.data?.error ||
                                                    (err as Error)?.message ||
                                                    'Could not finish import'
                                                toast.error(msg)
                                            }
                                        }}
                                    >
                                        Finish import
                                    </Button>
                                )}
                                <Button
                                    type='button'
                                    variant='outline'
                                    onClick={async () => {
                                        await clearImportSession()
                                        toast.success('Cleared stuck import — use Fill blanks to continue')
                                        resetForm()
                                        setTab('paste')
                                    }}
                                >
                                    Clear stuck import
                                </Button>
                                <Button type='button' variant='outline' onClick={handleClose}>
                                    Close (keeps running)
                                </Button>
                            </>
                        )}
                    </div>
                </div>
            )}
        </ResearchModalShell>
    )
}
